import {
  BinaryReader,
  ServerOp,
  decodeChatMessage,
  decodeCombat,
  decodeDespawn,
  decodeEffect,
  decodeEntityInfo,
  decodeGroundItems,
  decodeInventory,
  decodeLootFilter,
  encodeLootFilter,
  decodeAutomationState,
  encodeAutomationRequest,
  decodeBank,
  decodeMapChunk,
  decodeBestiary,
  decodeTileUpdate,
  encodeBestiaryRequest,
  encodeLook,
  encodeUseTile,
  decodePing,
  decodeQueue,
  decodeRedirect,
  decodeShop,
  decodeSocial,
  decodeTrade,
  decodeServerMessage,
  decodeSnapshot,
  decodeStats,
  decodeWelcome,
  encodeAttack,
  encodeAuth,
  encodeBankRequest,
  encodeCast,
  encodeChat,
  encodeInteract,
  encodeMove,
  encodeMoveItem,
  encodePing,
  encodeShopRequest,
  encodeSocialRequest,
  encodeStance,
  encodeTradeRequest,
  encodeUseItem,
  encodeUseOn,
  type ChatMessagePacket,
  type CombatPacket,
  type DespawnPacket,
  type Direction,
  type EffectPacket,
  type EntityInfoPacket,
  type GroundItemsPacket,
  type InventoryPacket,
  type AutomationRequestPacket,
  type AutomationStatePacket,
  type LootFilterPacket,
  type UseItemPacket,
  type MapChunkPacket,
  type BestiaryPacket,
  type TileUpdatePacket,
  type BankPacket,
  type BankRequestPacket,
  type ChatPacket,
  type MoveItemPacket,
  type CombatStance,
  type SocialAction,
  type SocialPacket,
  type QueuePacket,
  type RedirectPacket,
  type ServerMessagePacket,
  type ShopPacket,
  type ShopRequestPacket,
  type TradePacket,
  type TradeRequestPacket,
  type SnapshotPacket,
  type StatsPacket,
  type WelcomePacket,
} from '@game/protocol';

export interface ConnectionHandlers {
  onWelcome(packet: WelcomePacket): void;
  /** Região cheia: a posição na fila chega no lugar do Welcome, e repete quando muda. */
  onQueue(packet: QueuePacket): void;
  /** Continue noutra porta: a região de destino é outro processo (PRD §3.4). */
  onRedirect(packet: RedirectPacket): void;
  onMapChunk(packet: MapChunkPacket): void;
  /** Um tile mudou depois de o chunk dele já ter chegado (PRD §15.2). */
  onTileUpdate(packet: TileUpdatePacket): void;
  /** A ficha do bestiário, inteira (PRD §15.5). */
  onBestiary(packet: BestiaryPacket): void;
  onSnapshot(packet: SnapshotPacket): void;
  onDespawn(packet: DespawnPacket): void;
  onCombat(packet: CombatPacket): void;
  onStats(packet: StatsPacket): void;
  onEntityInfo(packet: EntityInfoPacket): void;
  onServerMessage(packet: ServerMessagePacket): void;
  onInventory(packet: InventoryPacket): void;
  onGroundItems(packet: GroundItemsPacket): void;
  /** A lista de auto-loot guardada no personagem (PRD §6). Chega uma vez, no `join`. */
  onLootFilter(packet: LootFilterPacket): void;
  /**
   * As outras automações — comida e caçada — e a rota gravada (PRD §6).
   *
   * Chega no `join` como a lista de auto-loot, **e a cada ponto gravado**: a rota é escrita
   * pelo servidor, então este pacote é a única forma de a tela saber que ela cresceu.
   */
  onAutomation(packet: AutomationStatePacket): void;
  onEffect(packet: EffectPacket): void;
  onChat(packet: ChatMessagePacket): void;
  onShop(packet: ShopPacket): void;
  onBank(packet: BankPacket): void;
  onTrade(packet: TradePacket): void;
  onSocial(packet: SocialPacket): void;
  onRtt(rttMs: number): void;
  onClose(): void;
}

/**
 * WebSocket do jogo. Toda leitura e escrita passa por `@game/protocol` — o cliente não
 * tem decoder próprio, então não existe a possibilidade de ele divergir do servidor.
 */
export class GameConnection {
  private socket: WebSocket | undefined;
  private seq = 0;
  private rttMs = 0;

  private readonly url: string;
  private readonly token: string;
  private readonly handlers: ConnectionHandlers;

  constructor(url: string, token: string, handlers: ConnectionHandlers) {
    this.url = url;
    this.token = token;
    this.handlers = handlers;
  }

  get latencyMs(): number {
    return this.rttMs;
  }

  get isOpen(): boolean {
    return this.socket?.readyState === WebSocket.OPEN;
  }

  connect(): void {
    const socket = new WebSocket(this.url);
    // Sem isto o navegador entrega `Blob`, que só se lê de forma assíncrona —
    // inviável para um frame que precisa ser processado no quadro atual.
    socket.binaryType = 'arraybuffer';
    this.socket = socket;

    // O token de sessão é o primeiro frame por contrato: antes dele a conexão não tem
    // personagem e o servidor a derruba por tempo.
    socket.addEventListener('open', () => {
      socket.send(encodeAuth({ token: this.token }).toUint8());
    });

    socket.addEventListener('message', (event) => {
      this.handleFrame(event.data as ArrayBuffer);
    });

    socket.addEventListener('close', () => this.handlers.onClose());
    socket.addEventListener('error', () => socket.close());
  }

  private handleFrame(buffer: ArrayBuffer): void {
    const reader = new BinaryReader(buffer);
    const opcode = reader.u8();

    switch (opcode) {
      case ServerOp.Welcome:
        this.handlers.onWelcome(decodeWelcome(reader));
        break;

      case ServerOp.Queue:
        this.handlers.onQueue(decodeQueue(reader));
        break;

      case ServerOp.Redirect:
        this.handlers.onRedirect(decodeRedirect(reader));
        break;

      case ServerOp.Shop:
        this.handlers.onShop(decodeShop(reader));
        break;

      case ServerOp.Bank:
        this.handlers.onBank(decodeBank(reader));
        break;

      case ServerOp.Trade:
        this.handlers.onTrade(decodeTrade(reader));
        break;

      case ServerOp.Social:
        this.handlers.onSocial(decodeSocial(reader));
        break;

      case ServerOp.MapChunk:
        this.handlers.onMapChunk(decodeMapChunk(reader));
        break;

      case ServerOp.TileUpdate:
        this.handlers.onTileUpdate(decodeTileUpdate(reader));
        break;

      case ServerOp.Bestiary:
        this.handlers.onBestiary(decodeBestiary(reader));
        break;

      case ServerOp.Snapshot:
        this.handlers.onSnapshot(decodeSnapshot(reader));
        break;

      case ServerOp.Despawn:
        this.handlers.onDespawn(decodeDespawn(reader));
        break;

      case ServerOp.Combat:
        this.handlers.onCombat(decodeCombat(reader));
        break;

      case ServerOp.Stats:
        this.handlers.onStats(decodeStats(reader));
        break;

      case ServerOp.EntityInfo:
        this.handlers.onEntityInfo(decodeEntityInfo(reader));
        break;

      case ServerOp.ServerMessage:
        this.handlers.onServerMessage(decodeServerMessage(reader));
        break;

      case ServerOp.Inventory:
        this.handlers.onInventory(decodeInventory(reader));
        break;

      case ServerOp.GroundItems:
        this.handlers.onGroundItems(decodeGroundItems(reader));
        break;

      case ServerOp.LootFilter:
        this.handlers.onLootFilter(decodeLootFilter(reader));
        break;

      case ServerOp.Automation:
        this.handlers.onAutomation(decodeAutomationState(reader));
        break;

      case ServerOp.Effect:
        this.handlers.onEffect(decodeEffect(reader));
        break;

      case ServerOp.Chat:
        this.handlers.onChat(decodeChatMessage(reader));
        break;

      case ServerOp.Pong: {
        const { clientTime } = decodePing(reader);
        // O servidor devolveu o carimbo intocado: o RTT sai do nosso próprio
        // relógio, sem precisar sincronizar horários entre as pontas.
        this.rttMs = Math.round(performance.now()) - clientTime;
        this.handlers.onRtt(this.rttMs);
        break;
      }

      default:
        console.warn(`[net] opcode desconhecido do servidor: 0x${opcode.toString(16)}`);
    }
  }

  /** Envia um input de movimento e devolve o `seq` atribuído, para a reconciliação. */
  sendMove(direction: Direction): number {
    if (!this.isOpen) return this.seq;

    this.seq += 1;
    this.socket?.send(encodeMove({ direction, seq: this.seq }).toUint8());
    return this.seq;
  }

  /**
   * Seleciona o alvo do auto-attack. `0` desmarca.
   *
   * Não é um golpe: a cadência de ataque é decidida no servidor, e mandar um pacote por
   * golpe deixaria a velocidade de ataque na mão do cliente.
   */
  sendAttack(targetId: number): void {
    this.socket?.send(encodeAttack({ targetId }).toUint8());
  }

  /**
   * Escolhe a postura de combate (PRD §5). A confirmação chega no `Stats`, e não aqui.
   *
   * Sai também logo depois do Welcome, com a preferência guardada no navegador: o servidor
   * põe todo mundo em `Balanced` na entrada, e sem este reenvio quem joga sempre em defesa
   * voltaria ao equilíbrio a cada login e a cada portal atravessado.
   */
  sendStance(stance: CombatStance): void {
    if (!this.isOpen) return;
    this.socket?.send(encodeStance({ stance }).toUint8());
  }

  sendCast(spellId: number, targetId = 0, x = 0, y = 0): void {
    if (!this.isOpen) return;
    this.socket?.send(encodeCast({ spellId, targetId, x, y }).toUint8());
  }

  sendChat(packet: ChatPacket): void {
    if (!this.isOpen) return;
    this.socket?.send(encodeChat(packet).toUint8());
  }

  sendSocial(action: SocialAction, name: string): void {
    if (!this.isOpen) return;
    this.socket?.send(encodeSocialRequest({ action, name }).toUint8());
  }

  sendMoveItem(packet: MoveItemPacket): void {
    if (!this.isOpen) return;
    this.socket?.send(encodeMoveItem(packet).toUint8());
  }

  /**
   * Usa um item: beber poção, vestir pelo clique, revistar um corpo.
   *
   * As coordenadas só significam alguma coisa quando `container` é o chão, e ausentes dizem
   * "o tile de baixo dos pés" — que é o que o gesto do inventário sempre quis dizer. Ver
   * `UseItemPacket`.
   */
  sendUseItem(container: number, slot: number, x?: number, y?: number): void {
    if (!this.isOpen) return;

    const packet: UseItemPacket = { container, slot };
    if (x !== undefined && y !== undefined) {
      packet.x = x;
      packet.y = y;
    }

    this.socket?.send(encodeUseItem(packet).toUint8());
  }

  /** Escolhe o que o auto-loot recolhe sozinho (PRD §6). A lista viaja inteira. */
  sendLootFilter(packet: LootFilterPacket): void {
    if (!this.isOpen) return;
    this.socket?.send(encodeLootFilter(packet).toUint8());
  }

  /**
   * Liga, desliga e ajusta a auto-comida e a auto-caçada (PRD §6).
   *
   * A rota **não** vai junto: ela é gravada pelo servidor, e o que sobe daqui é no máximo um
   * comando sobre ela. Ver `packets/automation.ts`.
   */
  sendAutomation(packet: AutomationRequestPacket): void {
    if (!this.isOpen) return;
    this.socket?.send(encodeAutomationRequest(packet).toUint8());
  }

  /** Usa um item **em alguma coisa**: a runa numa criatura ou num ponto do chão. */
  sendUseOn(container: number, slot: number, targetId = 0, x = 0, y = 0): void {
    if (!this.isOpen) return;
    this.socket?.send(encodeUseOn({ container, slot, targetId, x, y }).toUint8());
  }

  /** "O que há aqui?" (PRD §15.4). A resposta chega pelo `ServerMessage 0x88`. */
  sendLook(x: number, y: number, z: number, stackIndex = 0): void {
    if (!this.isOpen) return;
    this.socket?.send(encodeLook({ x, y, z, stackIndex }).toUint8());
  }

  /** Pede a ficha do bestiário (PRD §15.5). */
  sendBestiaryRequest(): void {
    if (!this.isOpen) return;
    this.socket?.send(encodeBestiaryRequest().toUint8());
  }

  /** Puxa alavanca, abre porta, abre baú — sem item na mão (PRD §15.4). */
  sendUseTile(x: number, y: number, z: number): void {
    if (!this.isOpen) return;
    this.socket?.send(encodeUseTile({ x, y, z }).toUint8());
  }

  /** Fala com um NPC. O servidor decide se abre loja, banco ou só uma linha de conversa. */
  sendInteract(npcId: number): void {
    if (!this.isOpen) return;
    this.socket?.send(encodeInteract({ npcId }).toUint8());
  }

  sendShop(packet: ShopRequestPacket): void {
    if (!this.isOpen) return;
    this.socket?.send(encodeShopRequest(packet).toUint8());
  }

  sendBank(packet: BankRequestPacket): void {
    if (!this.isOpen) return;
    this.socket?.send(encodeBankRequest(packet).toUint8());
  }

  sendTrade(packet: TradeRequestPacket): void {
    if (!this.isOpen) return;
    this.socket?.send(encodeTradeRequest(packet).toUint8());
  }

  sendPing(): void {
    if (!this.isOpen) return;
    const clientTime = Math.round(performance.now());
    this.socket?.send(encodePing({ clientTime }).toUint8());
  }

  close(): void {
    this.socket?.close();
  }
}
